import { CheckSquare2, ChevronLeft, ChevronRight, Filter, ListFilter, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { AgentTicketRow } from "../../components/AgentTicketRow";
import { EmptyState, PageError, PageLoader } from "../../components/StateViews";
import { useToast } from "../../context/ToastContext";
import { useSession } from "../../context/SessionContext";
import { usePagedCollection, COLLECTION_PAGE_SIZE } from "../../hooks/usePagedCollection";
import { CollectionPagination } from "../../components/CollectionPagination";
import { useRealtimeRoom } from "../../hooks/useTicketRealtime";
import { apiEnvelope, apiRequest, asTicketList, queryString } from "../../lib/api";
import type { Ticket, TicketStatus } from "../../lib/types";

const STATUS_OPTIONS: Array<{ value: TicketStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "on_hold", label: "On hold" },
  { value: "solved", label: "Solved" },
  { value: "closed", label: "Closed" },
];
const ALL_STATUS_VALUES = STATUS_OPTIONS.map((option) => option.value);
const DEFAULT_STATUS_VALUES: TicketStatus[] = ["new", "open", "pending", "on_hold"];
const STATUS_VALUES = new Set<TicketStatus>(ALL_STATUS_VALUES);
const PRIORITY_OPTIONS = ["all", "urgent", "high", "normal", "low"] as const;
const FILTER_STORAGE_PREFIX = "nextcart-support:agent-ticket-filters:";

interface SavedTicketFilters {
  statuses?: TicketStatus[];
  priority?: string;
  assignee?: string;
}

function parseStatuses(value: string | null): TicketStatus[] | null {
  if (value === null) return null;
  const statuses = [...new Set(value.split(",").filter((status): status is TicketStatus => STATUS_VALUES.has(status as TicketStatus)))];
  return statuses.length ? statuses : null;
}

function loadSavedFilters(staffId?: string): SavedTicketFilters {
  if (!staffId) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(`${FILTER_STORAGE_PREFIX}${staffId}`) ?? "{}") as SavedTicketFilters;
    const statuses = Array.isArray(parsed.statuses)
      ? parsed.statuses.filter((status): status is TicketStatus => STATUS_VALUES.has(status as TicketStatus))
      : undefined;
    return {
      statuses: statuses?.length ? statuses : undefined,
      priority: PRIORITY_OPTIONS.includes(parsed.priority as typeof PRIORITY_OPTIONS[number]) ? parsed.priority : undefined,
      assignee: ["all", "me", "unassigned"].includes(parsed.assignee ?? "") ? parsed.assignee : undefined,
    };
  } catch {
    return {};
  }
}
const PAGE_SIZE = COLLECTION_PAGE_SIZE;

export function AgentTicketsPage() {
  const [params, setParams] = useSearchParams();
  const [queryInput, setQueryInput] = useState(params.get("query") ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [queueUpdated, setQueueUpdated] = useState(false);
  const queueRefreshTimerRef = useRef<number | undefined>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const { user } = useSession();
  const query = params.get("query") ?? "";
  const savedFilters = loadSavedFilters(user?.id);
  const statuses = parseStatuses(params.get("status")) ?? savedFilters.statuses ?? DEFAULT_STATUS_VALUES;
  const statusKey = statuses.join(",");
  const statusQuery = statuses.length === ALL_STATUS_VALUES.length ? undefined : statusKey;
  const priority = params.get("priority") ?? savedFilters.priority ?? "all";
  const assignee = params.get("assignee") ?? savedFilters.assignee ?? "all";
  const page = Math.max(1, Math.floor(Number(params.get("page")) || 1));

  useEffect(() => { const timer = window.setTimeout(() => updateParam("query", queryInput.trim() || null), 300); return () => window.clearTimeout(timer); }, [queryInput]);
  useEffect(() => {
    if (params.get("focus") === "search") searchRef.current?.focus();
    const shortcut = (event: KeyboardEvent) => { if (event.key === "/" && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) { event.preventDefault(); searchRef.current?.focus(); } };
    window.addEventListener("keydown", shortcut); return () => window.removeEventListener("keydown", shortcut);
  }, []);

  const result = usePagedCollection<Ticket>(
    JSON.stringify([query, statusKey, priority, assignee]), page,
    nextPage => updateParam("page", nextPage === 1 ? null : String(nextPage), true),
    async (page, signal) => {
      const response = await apiEnvelope<unknown>(`/agent/tickets${queryString({ query: query || undefined, status: statusQuery, priority: priority === "all" ? undefined : priority, assignee: assignee === "all" ? undefined : assignee, page, limit: PAGE_SIZE })}`, { signal });
      const data = asTicketList(response.data, response.meta);
      return { items: data.tickets, total: data.meta.total ?? data.tickets.length };
    },
  );
  const total = result.total;
  function requestQueueRefresh() {
    if (page !== 1) {
      setQueueUpdated(true);
      return;
    }
    if (queueRefreshTimerRef.current) window.clearTimeout(queueRefreshTimerRef.current);
    queueRefreshTimerRef.current = window.setTimeout(() => { void result.refresh(); }, 350);
  }

  useRealtimeRoom("team:all", requestQueueRefresh);

  useEffect(() => {
    const refreshVisibleQueue = () => {
      if (document.visibilityState === "visible" && page === 1) void result.refresh();
    };
    const timer = window.setInterval(refreshVisibleQueue, 30_000);
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      if (page === 1) void result.refresh(); else setQueueUpdated(true);
    };
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [page, result.refresh]);

  useEffect(() => {
    setQueueUpdated(false);
  }, [query, statusKey, priority, assignee]);

  useEffect(() => () => {
    if (queueRefreshTimerRef.current) window.clearTimeout(queueRefreshTimerRef.current);
  }, [result.refresh]);
  function updateParam(key: string, value?: string | null, keepPage = false) {
    if (key === "query" && (value || "") === (params.get("query") || "")) return;
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key); else next.set(key, value);
    if (!keepPage && key !== "page") next.delete("page");
    setParams(next, { replace: key === "query" });
    setSelected(new Set());
  }

  function persistFilters(next: Partial<SavedTicketFilters>) {
    if (!user?.id) return;
    try {
      localStorage.setItem(`${FILTER_STORAGE_PREFIX}${user.id}`, JSON.stringify({ statuses, priority, assignee, ...next }));
    } catch {
      // The active URL still retains the filter when storage is unavailable.
    }
  }

  function updateStatus(value: TicketStatus, checked: boolean) {
    const selectedStatuses = new Set(statuses);
    if (checked) selectedStatuses.add(value); else selectedStatuses.delete(value);
    if (!selectedStatuses.size) return;
    const nextStatuses = ALL_STATUS_VALUES.filter((status) => selectedStatuses.has(status));
    persistFilters({ statuses: nextStatuses });
    updateParam("status", nextStatuses.join(","));
  }

  function updateSavedParam(key: "priority" | "assignee", value: string) {
    persistFilters({ [key]: value });
    updateParam(key, value);
  }
  async function bulkUpdate(action: "assign_me" | "solved" | "closed") {
    if (!selected.size) return; setBulkBusy(true);
    try {
      await apiRequest("/agent/tickets/bulk", { method: "POST", body: action === "assign_me" ? { ticketIds: [...selected], assigneeId: user?.id } : { ticketIds: [...selected], status: action } });
      showToast(`${selected.size} ticket${selected.size === 1 ? "" : "s"} updated.`, "success"); setSelected(new Set()); await result.refresh();
    } catch (error) { showToast(error instanceof Error ? error.message : "Bulk update failed.", "error"); } finally { setBulkBusy(false); }
  }

  const tickets = result.items;
  const allSelected = tickets.length > 0 && tickets.every((ticket) => selected.has(ticket.id));
  return (
    <section ref={result.topRef} className="agent-page page-stack agent-ticket-index">
      <header className="agent-page-header"><div><p className="eyebrow">Ticket workspace</p><h1>Tickets</h1><p>Prioritize, assign, and resolve customer conversations.</p></div><div className="agent-page-header__actions"><span className="result-count">{total} results</span><Link className="button button--primary" to="/agent/tickets/new"><Plus aria-hidden="true" size={18} /> Create ticket</Link></div></header>
      <div className="agent-ticket-tools">
        <label className="search-field card"><Search aria-hidden="true" size={18} /><span className="sr-only">Search tickets</span><input ref={searchRef} value={queryInput} onChange={(event) => setQueryInput(event.target.value)} placeholder="Search subject, requester, email, or number…" /><kbd>/</kbd></label>
        <button className="button button--secondary mobile-only" type="button" onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}><ListFilter aria-hidden="true" size={18} /> Filters</button>
      </div>
      <div className={`agent-filterbar card${filtersOpen ? " is-open" : ""}`}>
        <fieldset className="ticket-status-filter">
          <legend>Status</legend>
          <div className="ticket-status-filter__options">
            {STATUS_OPTIONS.map((option) => <label key={option.value}><input type="checkbox" checked={statuses.includes(option.value)} disabled={statuses.length === 1 && statuses[0] === option.value} onChange={(event) => updateStatus(option.value, event.target.checked)} /><span>{option.label}</span></label>)}
          </div>
        </fieldset>
        <label className="compact-select"><span>Priority</span><select value={priority} onChange={(event) => updateSavedParam("priority", event.target.value)}>{PRIORITY_OPTIONS.map((value) => <option key={value} value={value}>{value === "all" ? "All priorities" : value}</option>)}</select></label>
        <label className="compact-select"><span>Assignment</span><select value={assignee} onChange={(event) => updateSavedParam("assignee", event.target.value)}><option value="all">Everyone</option><option value="me">Assigned to me</option><option value="unassigned">Unassigned</option></select></label>
      </div>
      {queueUpdated && <button className="queue-update-notice" type="button" onClick={() => { setQueueUpdated(false); setSelected(new Set()); result.showNewest(); }}><span>New or updated tickets are available.</span> Show newest</button>}
      {selected.size > 0 && <div className="bulk-bar" role="toolbar" aria-label="Bulk ticket actions"><span><CheckSquare2 aria-hidden="true" size={17} /> {selected.size} selected</span><button type="button" disabled={bulkBusy} onClick={() => void bulkUpdate("assign_me")}>Assign to me</button><button type="button" disabled={bulkBusy} onClick={() => void bulkUpdate("solved")}>Mark solved</button><button type="button" disabled={bulkBusy} onClick={() => void bulkUpdate("closed")}>Close ticket</button><button type="button" onClick={() => setSelected(new Set())}>Clear</button></div>}
      {result.loading && !tickets.length ? <PageLoader label="Loading ticket queue…" /> : result.error ? <PageError message={result.error} onRetry={() => void result.refresh()} /> : tickets.length ? <div className="agent-ticket-table card">
        <div className="agent-ticket-table__head" role="row"><label><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(tickets.map((ticket) => ticket.id)))} aria-label="Select all tickets" /></label><span>Ticket</span><span>Requester</span><span>Status</span><span>Priority</span><span>Assignee</span><span>Updated</span></div>
        {tickets.map((ticket) => <AgentTicketRow key={ticket.id} ticket={ticket} selected={selected.has(ticket.id)} onSelect={() => setSelected((current) => {
          const next = new Set(current);
          next.has(ticket.id) ? next.delete(ticket.id) : next.add(ticket.id);
          return next;
        })} />)}
      </div> : <EmptyState title="No tickets found" description="Adjust your filters or search terms to see more of the queue." icon={<Filter aria-hidden="true" size={26} />} />}
      <CollectionPagination {...result} busy={result.refreshing} count={tickets.length} label="Ticket pages" />
      <Link className="mobile-create-ticket mobile-only" to="/agent/tickets/new" aria-label="Create ticket"><Plus aria-hidden="true" /></Link>
    </section>
  );
}
